// Background service for sending alerts continuously
// This runs in the background even when the Alerts page is closed

import { emailApi, EmailAlert } from './emailService';

const ALERT_CHECK_INTERVAL = 3000; // Check every 3 seconds
const SENT_ALERTS_KEY = 'sentAlertKeys';
const ALERT_CACHE_DURATION = 3600000; // 1 hour

interface BackgroundAlert {
  id: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  entity: string;
  message: string;
  machineId?: string;
  sensorId?: string;
  timestamp: string;
  twinId: string;
}

class AlertBackgroundService {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private sentAlerts = new Set<string>();
  private currentAlerts: BackgroundAlert[] = [];
  private userEmail: string | null = null;
  private twinData: any = null;

  constructor() {
    this.loadSentAlerts();
  }

  private loadSentAlerts() {
    try {
      const saved = localStorage.getItem(SENT_ALERTS_KEY);
      if (saved) {
        this.sentAlerts = new Set(JSON.parse(saved));
      }
    } catch (e) {
      console.error('[AlertService] Failed to load sent alerts:', e);
    }
  }

  private saveSentAlerts() {
    try {
      localStorage.setItem(SENT_ALERTS_KEY, JSON.stringify(Array.from(this.sentAlerts)));
    } catch (e) {
      console.error('[AlertService] Failed to save sent alerts:', e);
    }
  }

  public setUserEmail(email: string | null) {
    this.userEmail = email;
  }

  public setTwinData(twinData: any) {
    this.twinData = twinData;
  }

  private generateAlerts(): BackgroundAlert[] {
    if (!this.twinData?.machines) return [];

    const alerts: BackgroundAlert[] = [];
    const twin = this.twinData;

    twin.machines.forEach((machine: any) => {
      if (machine.status === 'fault') {
        alerts.push({
          id: `${machine.machineId}-status-fault`,
          severity: 'CRITICAL',
          entity: machine.name,
          message: `Machine status: FAULT`,
          machineId: machine.machineId,
          timestamp: new Date().toLocaleTimeString(),
          twinId: twin.id || 'unknown',
        });
      }

      machine.sensors?.forEach((sensor: any) => {
        const simValue = twin.simulatedMachineData?.[machine.machineId]?.[sensor.sensorId];
        const value = simValue !== undefined ? simValue : sensor.value;
        const range = sensor.max - sensor.min;
        const threshold95 = sensor.min + range * 0.95;
        const threshold80 = sensor.min + range * 0.80;

        if (value > threshold95) {
          alerts.push({
            id: `${machine.machineId}-${sensor.sensorId}-critical`,
            severity: 'CRITICAL',
            entity: machine.name,
            message: `${sensor.name} CRITICAL: ${value.toFixed(1)}${sensor.unit}`,
            machineId: machine.machineId,
            sensorId: sensor.sensorId,
            timestamp: new Date().toLocaleTimeString(),
            twinId: twin.id || 'unknown',
          });
        } else if (value > threshold80) {
          alerts.push({
            id: `${machine.machineId}-${sensor.sensorId}-warning`,
            severity: 'WARNING',
            entity: machine.name,
            message: `${sensor.name} WARNING: ${value.toFixed(1)}${sensor.unit}`,
            machineId: machine.machineId,
            sensorId: sensor.sensorId,
            timestamp: new Date().toLocaleTimeString(),
            twinId: twin.id || 'unknown',
          });
        }
      });
    });

    return alerts;
  }

  private async sendAlert(alert: BackgroundAlert) {
    if (!this.userEmail) {
      console.log('[AlertService] No user email, skipping');
      return;
    }

    const alertKey = `${alert.id}-${alert.severity}`;
    if (this.sentAlerts.has(alertKey)) {
      console.log('[AlertService] Already sent this alert:', alertKey);
      return;
    }

    // Get preferences
    const preferences = this.getPreferences();
    const shouldSend =
      (alert.severity === 'CRITICAL' && preferences.emailOnCritical) ||
      (alert.severity === 'WARNING' && preferences.emailOnWarning) ||
      (alert.severity === 'INFO' && preferences.emailOnInfo);

    if (!shouldSend) {
      console.log('[AlertService] Email disabled for severity:', alert.severity);
      return;
    }

    this.sentAlerts.add(alertKey);
    this.saveSentAlerts();

    const emailAlert: EmailAlert = {
      to: this.userEmail,
      subject: `🚨 ${alert.severity} Alert: ${alert.entity}`,
      severity: alert.severity,
      entity: alert.entity,
      message: alert.message,
      machineId: alert.machineId,
      sensorId: alert.sensorId,
      timestamp: alert.timestamp,
      twinId: alert.twinId,
    };

    console.log('[AlertService] Sending alert to primary email:', this.userEmail);
    try {
      await emailApi.sendAlert(emailAlert);
    } catch (e) {
      console.error('[AlertService] Failed to send to primary email:', e);
    }

    // Send to additional emails
    if (preferences.notificationEmails && preferences.notificationEmails.length > 0) {
      console.log('[AlertService] Sending to additional emails:', preferences.notificationEmails);
      for (const email of preferences.notificationEmails) {
        try {
          await emailApi.sendAlert({ ...emailAlert, to: email });
          console.log('[AlertService] Sent to additional email:', email);
        } catch (e) {
          console.error(`[AlertService] Failed to send to ${email}:`, e);
        }
      }
    }

    // Auto-remove from sent alerts after 1 hour
    setTimeout(() => {
      this.sentAlerts.delete(alertKey);
      this.saveSentAlerts();
    }, ALERT_CACHE_DURATION);
  }

  private getPreferences() {
    try {
      const saved = localStorage.getItem('alertPreferences');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('[AlertService] Failed to load preferences:', e);
    }

    return {
      emailOnCritical: true,
      emailOnWarning: true,
      emailOnInfo: false,
      notificationEmails: [],
    };
  }

  private async checkAndSendAlerts() {
    const alerts = this.generateAlerts();

    for (const alert of alerts) {
      await this.sendAlert(alert);
    }
  }

  public start() {
    if (this.intervalId) {
      console.log('[AlertService] Already running');
      return;
    }

    console.log('[AlertService] Starting background alert service');
    this.intervalId = setInterval(() => {
      this.checkAndSendAlerts().catch(e => {
        console.error('[AlertService] Error checking alerts:', e);
      });
    }, ALERT_CHECK_INTERVAL);
  }

  public stop() {
    if (this.intervalId) {
      console.log('[AlertService] Stopping background alert service');
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  public clearCache() {
    console.log('[AlertService] Clearing sent alerts cache');
    this.sentAlerts.clear();
    this.saveSentAlerts();
  }
}

// Export singleton instance
export const alertBackgroundService = new AlertBackgroundService();
